/**
 * KB-META
 * Purpose: App topbar
 * Role: ui.component
 * EntryPoints: and(), constructor(), if(), toggleDarkMode()
 * Inputs: max-width:768px; public layoutService:LayoutService; private userService:UserService; private router:Router; private tourService:CentralizedTourService; private breadcrumbService:BreadcrumbService; this.aiAssistantChatbot:unknown
 * Outputs: 
 * Validation: 
 * Dependencies: LayoutService, UserService, Router, CentralizedTourService, BreadcrumbService
 * Contracts: 
 * Auth&Privacy: auth:none; hipaa:none; pii:none
 * Observability: 
 * Router: shard=partner-portal-cura; H2=component/app.topbar; tags=ui
 * Owner: ; LastReviewed: 2025-08-25
 */
import { Component, NgZone, ViewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { Popover, PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';
import { LayoutService } from '../service/layout.service';
import { UserDetails } from '../../models/user.model';
import { UserService } from '../../services/user.service';
import { CentralizedTourService } from '../../services/centralized-tour.service';
import { SplitButtonModule } from 'primeng/splitbutton';
import { ButtonModule } from 'primeng/button';
import { TourPrimeNgModule } from 'ngx-ui-tour-primeng';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { BreadcrumbService, Breadcrumb, MenuItem as BreadcrumbMenuItem } from '../../services/breadcrumb.service';
import { HelpModalComponent } from '../../views/common/help-modal/help-modal.component';
import { AIAssistantChatbotComponent } from '../../views/common/ai-assistant-chatbot/ai-assistant-chatbot.component';
import { PhoneNumberPipe } from '../../pipes/phone-number.pipe';
import { AppLoaderService } from '../../services/app-loader.service';
import { CardStateService } from '../../services/card-state.service';
import { AppConfigurator } from './app.configurator';

@Component({
    selector: 'app-topbar',
    standalone: true,
    imports: [
        RouterModule, 
        CommonModule, 
        StyleClassModule, 
        PopoverModule, 
        TooltipModule,
        SplitButtonModule,
        ButtonModule,
        TourPrimeNgModule,
        BreadcrumbModule,
        HelpModalComponent,
        AIAssistantChatbotComponent,
        AppConfigurator
        ,PhoneNumberPipe
    ],
    template: ` <div class="layout-topbar">
        <div class="layout-topbar-logo-container">
            <button class="layout-menu-button layout-topbar-action" (click)="layoutService.onMenuToggle()">
                <i class="pi pi-bars"></i>
            </button>
            <img src="assets/images/cura-image2.jpg" alt="logo" height="32" width="182"/>
        </div>

        <div class="layout-topbar-actions">
            <div class="layout-config-menu flex items-center gap-2">

                <!-- Theme Configurator Dropdown -->
                <div class="relative">
                    <button class="layout-topbar-action" pStyleClass="@next" enterFromClass="hidden" enterActiveClass="animate-scalein" leaveToClass="hidden" leaveActiveClass="animate-fadeout" [hideOnOutsideClick]="true" pTooltip="Theme & Layout" tooltipPosition="bottom" tooltipDelay="500">
                        <i class="pi pi-palette"></i>
                    </button>
                    <app-configurator></app-configurator>
                </div>

                <p-splitButton 
                    *ngIf="toursLoaded && hasTourAvailable" 
                    label="Tour" 
                    icon="pi pi-question-circle" 
                    (onClick)="startIntroTour()" 
                    [model]="tourItems" 
                >
                </p-splitButton>
                
                <!-- AI Assistant Button -->
                <button
                    class="layout-topbar-action ai-assistant-button"
                    (click)="openAIAssistant()"
                    pTooltip="AI Assistant - Get help with system, content, or onboarding"
                    tooltipPosition="bottom"
                    tooltipDelay="500">
                    <i class="pi pi-robot"></i>
                </button>
                
            </div>

            <button class="layout-topbar-menu-button layout-topbar-action" pStyleClass="@next" enterFromClass="hidden" enterActiveClass="animate-scalein" leaveToClass="hidden" leaveActiveClass="animate-fadeout" [hideOnOutsideClick]="true">
                <i class="pi pi-ellipsis-v"></i>
            </button>

            <div class="layout-topbar-menu hidden lg:block">
                <div class="layout-topbar-menu-content">
                    
                    <button type="button" class="layout-topbar-action" (click)="toggle($event)">
                        <i class="pi pi-user"></i>
                        <span>Profile</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
    <div class="layout-breadcrumb-container">
        <div class="layout-breadcrumb-wrapper">
            <button *ngIf="canGoBack" type="button" class="p-button-text mr-2" pButton icon="pi pi-arrow-left" (click)="navigateBack()"></button>
            <p-breadcrumb [model]="breadcrumbItems" [home]="home" styleClass="border-none bg-transparent p-0"></p-breadcrumb>
        </div>
        <div class="layout-breadcrumb-right">
            <div class="user-greeting">
                <label>
                    <b>
                        Hello, {{userName}}
                    </b>
                </label>
            </div>
        </div>
    </div>
    <p-popover #op>
        <div class="flex flex-col gap-4">
            <div>
                
                <ul class="list-none p-0 m-0 flex flex-col">
                    
                    <li class="flex items-center gap-2 px-2 py-3 hover:bg-emphasis cursor-pointer rounded-border">
                        <h6>
                        Email : {{partnerEmail}}
                        </h6>
                    </li>
                    <li class="flex items-center gap-2 px-2 py-3 hover:bg-emphasis cursor-pointer rounded-border">
                        <h6>
                        Phone :  {{ partnerPhone | phoneNumber }}
                        </h6>
                    </li>
                    <li class="flex items-center gap-2 px-2 py-3 hover:bg-emphasis cursor-pointer rounded-border">
                        <h6>
                        Account
                        </h6>
                    </li>
                    <li class="flex items-center gap-2 px-2 py-3 hover:bg-emphasis cursor-pointer rounded-border" (click)="redirectToPage('profile')">
                        <button>
                            <i class="pi pi-user"></i>
                            Profile
                        </button>
                    </li>
                    <li class="flex items-center gap-2 px-2 py-3 hover:bg-emphasis cursor-pointer rounded-border" (click)="redirectToPage('change-password')">
                        <button>
                            <i class="pi pi-key"></i>
                            Change Password
                        </button>
                    </li>
                    <li class="flex items-center gap-2 px-2 py-3 hover:bg-emphasis cursor-pointer rounded-border" routerLink="/login">
                        <button>
                            <i class="pi pi-sign-out"></i>
                            logout
                        </button>
                    </li>
                </ul>
            </div>
        </div>
    </p-popover>
    
    <!-- Tour Step Template for global use -->
    <tour-step-template></tour-step-template>
    
    <!-- Help Modal Component -->
    <app-help-modal [menuData]="menuData" #helpModal></app-help-modal>
    
    <!-- AI Assistant Chatbot Component -->
    <app-ai-assistant-chatbot #aiAssistantChatbot></app-ai-assistant-chatbot>
    `,
    styles: [`
        .layout-breadcrumb-container {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            padding: 0.5rem 1rem;
        }
        .layout-breadcrumb-wrapper {
            display: flex;
            align-items: center;
            flex: 1 1 auto;
        }
        .layout-breadcrumb-right {
            display: flex;
            align-items: center;
            gap: 1rem;
            flex-wrap: wrap;
        }
        .breadcrumb-label {
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            background-color: var(--surface-card);
            box-shadow: 0 1px 2px rgba(0,0,0,0.1);
        }
        @media screen and (max-width: 768px) {
            .layout-breadcrumb-container {
                padding: 0.5rem;
            }
            .layout-breadcrumb-right {
                width: 100%;
                justify-content: space-between;
                margin-top: 0.5rem;
            }
        }
        .ai-assistant-button {
            position: relative;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border: none;
            border-radius: 8px;
            padding: 8px 12px;
            box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
            transition: all 0.3s ease;
        }

        .ai-assistant-button:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
            background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
        }

        .ai-assistant-button i {
            color: #ffffff;
            font-size: 1.2rem;
            transition: all 0.3s ease;
        }

        .ai-assistant-button:hover i {
            color: #f0f9ff;
            text-shadow: 0 0 8px rgba(255, 255, 255, 0.5);
        }
    `]
})
export class AppTopbar {
    items!: MenuItem[];
    @ViewChild('op') op!: Popover;
    @ViewChild('helpModal') helpModal!: HelpModalComponent;
    @ViewChild('aiAssistantChatbot') aiAssistantChatbot!: AIAssistantChatbotComponent;
    userDetails!: UserDetails | null;
    userProfile: string = '';
    partnerName: string = '';
    userName: string = '';
    partnerEmail: string = '';
    partnerPhone: string = '';
    hasTourAvailable: boolean = false;
    toursLoaded: boolean = false;
    tourItems: MenuItem[] = [];
    menuData: BreadcrumbMenuItem[] = []; // Using the MenuItem interface from BreadcrumbService
    currentStatus: string = 'Active'; // Default status, you can update this based on your requirements
    
    // Breadcrumb related properties
    breadcrumbItems: MenuItem[] = [];
    home: MenuItem = { icon: 'pi pi-home', command: () => this.goHome() };
    canGoBack: boolean = false;
    private currentUrl: string = '';
    private inPatientEditV2: boolean = false;
    private dashboardPrefetchPromise: Promise<unknown> | null = null;
    private dashboardNavAttempt: number = 0;
    private isNavigatingToDashboard: boolean = false;
    private dashboardRenderCheckToken: number = 0;
    private dashboardRescueInFlight: boolean = false;
    private dashboardRescueCooldownUntilTs: number = 0;
    private cancelRenderCheckToken: number = 0;
    private dashboardNavStartToken: number = 0;
    private patientEditRenderCheckToken: number = 0;
    private patientEditRescueInFlight: boolean = false;
    private patientEditRescueCooldownUntilTs: number = 0;
    private patientEditHealthCheckToken: number = 0;

    constructor(
        public layoutService: LayoutService,
        private userService: UserService,
        private router: Router,
        private zone: NgZone,
        private tourService: CentralizedTourService,
        private breadcrumbService: BreadcrumbService,
        private appLoader: AppLoaderService,
        private cardState: CardStateService
    ) {
        // Track current route so back-arrow logic is stable/reliable.
        this.currentUrl = String(this.router?.url || '');
        this.inPatientEditV2 = this.isPatientEditV2Url(this.currentUrl);
        if (this.inPatientEditV2) {
            this.prefetchDashboardChunk();
        }
        this.router.events.subscribe((evt) => {
            if (evt instanceof NavigationStart) {
                // If a navigation toward the dashboard starts, keep a short-lived watcher:
                // if the router-outlet ends up blank while the URL still shows Patient Edit,
                // automatically trigger a single rescue navigation (no second click needed).
                const target = String((evt as any)?.url || '');
                const isDashboardIntent =
                    target.includes('care-manager-dashboard') || this.isGenusConnectRootRoute(target);
                if (isDashboardIntent) {
                    const token = ++this.dashboardNavStartToken;
                    this.scheduleDashboardStartRescue(token);
                }
                return;
            }
            if (evt instanceof NavigationEnd) {
                this.currentUrl = String(evt.urlAfterRedirects || evt.url || '');
                this.inPatientEditV2 = this.isPatientEditV2Url(this.currentUrl);
                this.stopDashboardNavLoader();
                // If the outlet ends up blank on dashboard navigation, automatically "rescue" once.
                // This addresses the observed UX where the dashboard only appears after a second click.
                this.scheduleDashboardRenderRescueIfNeeded();
                if (this.inPatientEditV2) {
                    this.prefetchDashboardChunk();
                    this.schedulePatientEditRenderRescueIfNeeded();
                    this.startPatientEditHealthCheck();
                } else {
                    this.stopPatientEditHealthCheck();
                }
                return;
            }
            if (evt instanceof NavigationError) {
                this.stopDashboardNavLoader();
                const errorTarget = String((evt as any)?.url || '');
                if (this.isPatientEditV2Url(errorTarget)) {
                    this.schedulePatientEditCancelRescue(errorTarget);
                }
                return;
            }
            if (evt instanceof NavigationCancel) {
                // IMPORTANT:
                // We've observed a rare-but-severe UX bug where a canceled navigation can leave the
                // primary router-outlet empty (blank content area) while topbar/footer remain.
                // Users then "fix" it by clicking Home a second time.
                //
                // We detect a canceled dashboard navigation and auto-rescue if the outlet is blank.
                const cancelledTarget = String((evt as any)?.url || '');
                const cancelToDashboard =
                    cancelledTarget.includes('care-manager-dashboard') || this.isGenusConnectRootRoute(cancelledTarget);

                // If the user canceled a navigation that (should have) landed on the dashboard, run a short
                // post-cancel render check. Only if the main outlet is actually blank do we perform a rescue.
                let isDirty = false;
                try { isDirty = this.cardState?.isAnyDirty?.() === true; } catch { isDirty = false; }
                if (cancelToDashboard) {
                    // Keep (or re-show) the loader briefly while we confirm whether the outlet rendered.
                    try { this.appLoader.start('nav-dashboard', 'Returning to dashboard…'); } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }
                    this.scheduleDashboardCancelRescue();
                    return;
                }
                if (this.isPatientEditV2Url(cancelledTarget)) {
                    try { this.appLoader.start('patient-edit', 'Restoring patient edit…'); } catch {}
                    this.schedulePatientEditCancelRescue(cancelledTarget);
                    return;
                }

                // Non-dashboard cancels, or dirty-blocked cancels: stop loader normally.
                this.stopDashboardNavLoader();
                return;
            }
        });

        this.userDetails = this.userService.initializeUserDetails();
        if (this.userDetails) {
            this.userProfile = this.userDetails.profile;
            this.partnerName = this.userDetails.preferred_username;
            this.partnerPhone = this.userDetails.phone_number;
            this.partnerEmail = this.userDetails.email;
            this.userName = this.userDetails.given_name;
            // Persist theme config per user using a scoped storage key
            const storageId = this.partnerEmail || this.partnerName || 'guest';
            this.layoutService.setStorageKey(`partner-portal-cura:layout-config:${storageId}`);
        }
        
        // Setup tour menu items
        this.initTourItems();
        
        // Check if tours are loaded
        this.checkToursLoaded();
        
        // Subscribe to breadcrumb changes
        this.breadcrumbService.breadcrumbs$.subscribe(breadcrumbs => {
            this.updateBreadcrumbItems(breadcrumbs);
            // Check if we can go back
            this.canGoBack = this.breadcrumbService.canGoBack();
        });
        
        // Get menu data from breadcrumb service if available
        this.breadcrumbService.getMenuData().subscribe(menuData => {
            this.menuData = menuData;
        });
    }
    
    // Update breadcrumb items from the breadcrumb service
    private updateBreadcrumbItems(breadcrumbs: Breadcrumb[]): void {
        this.breadcrumbItems = breadcrumbs.map(breadcrumb => {
            // While on Patient Edit, make the "Genus Connect" crumb behave like Home:
            // direct dashboard navigation + one-time refresh + fallback.
            if (this.inPatientEditV2 && String(breadcrumb.url || '').trim() === '/genus-connect') {
                return {
                    label: breadcrumb.label,
                    command: () => this.navigateToDashboardWithFallback()
                };
            }
            return {
                label: breadcrumb.label,
                routerLink: breadcrumb.url
            };
        });
    }
    
    // Navigate back using the breadcrumb service
    navigateBack(): void {
        // Patient Edit (Beta): always return to dashboard (avoid blank return states)
        if (this.inPatientEditV2 || this.isPatientEditV2Route()) {
            this.navigateToDashboardWithFallback();
            return;
        }

        this.breadcrumbService.goBack();
    }

    private goHome(): void {
        this.navigateToDashboardWithFallback();
    }

    private navigateToDashboardWithFallback(): void {
        // Prevent rapid repeat clicks from canceling the first navigation.
        if (this.isNavigatingToDashboard) {
            return;
        }

        this.zone.run(() => {
            // Always land on the Care Manager Dashboard.
            // If we're leaving Patient Edit, force a one-time dashboard refresh to avoid blank state.
            if (this.inPatientEditV2 || this.isPatientEditV2Route()) {
                this.markDashboardReturnRefreshOnce();
            }

            // Show a global loader so the content area doesn't appear "blank" while the dashboard chunk loads.
            try {
                this.appLoader.start('nav-dashboard', 'Returning to dashboard…');
            } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }

            this.isNavigatingToDashboard = true;

            // SPA navigation only (no hard reload). The loader stays up until NavigationEnd/Error.
            const tree = this.router.createUrlTree(['/genus-connect', 'care-manager-dashboard']);
            this.dashboardNavAttempt++;
            const attempt = this.dashboardNavAttempt;

            this.router
                .navigateByUrl(tree)
                .then((ok) => {
                    if (ok) {
                        // Extra safety: ensure loader/key is cleared even if router events are missed.
                        this.stopDashboardNavLoader();
                        return;
                    }

                    // Smooth "first-click" hardening:
                    // If navigation was canceled for a non-guard reason, retry once automatically.
                    if (attempt > 1) {
                        this.stopDashboardNavLoader();
                        return;
                    }
                    if (this.cardState?.isAnyDirty?.() === true) {
                        // Navigation cancel due to unsaved-changes guard should not leave the loader stuck.
                        this.stopDashboardNavLoader();
                        return;
                    }
                    try {
                        const curr = String(this.router?.url || this.currentUrl || '');
                        if (curr.includes('care-manager-dashboard')) {
                            this.stopDashboardNavLoader();
                            return;
                        }
                    } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }
                    setTimeout(() => {
                        try { this.appLoader.start('nav-dashboard', 'Returning to dashboard…'); } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }
                        this.zone.run(() => {
                            this.router
                                .navigateByUrl(tree)
                                .then((ok2) => {
                                    if (!ok2) {
                                        this.stopDashboardNavLoader();
                                    }
                                })
                                .catch(() => this.stopDashboardNavLoader());
                        });
                    }, 0);
                })
                .catch(() => {
                    // If navigation fails, just stop loader; appConfig already has a global
                    // chunk-load recovery that reloads on NavigationError when needed.
                    this.stopDashboardNavLoader();
                });
        });
    }

    private stopDashboardNavLoader(): void {
        this.dashboardNavAttempt = 0;
        this.isNavigatingToDashboard = false;
        try {
            this.appLoader.stop('nav-dashboard');
        } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }
    }

    private isDashboardRoute(url: string): boolean {
        const u = String(url || '');
        return u.includes('care-manager-dashboard');
    }

    private isGenusConnectRootRoute(url: string): boolean {
        // Treat "/genus-connect" as a dashboard-intent route (it redirects to the dashboard).
        // Normalize by stripping query/hash and any trailing slash.
        try {
            const raw = String(url || '');
            const base = raw.split('?')[0].split('#')[0].replace(/\/+$/, '');
            return base === '/genus-connect';
        } catch {
            return false;
        }
    }

    private scheduleDashboardStartRescue(token: number): void {
        const MAX_TICKS = 30; // ~3s at 100ms
        const DELAY_MS = 100;
        let ticks = 0;
        let blankStreak = 0;

        const tick = () => {
            if (token !== this.dashboardNavStartToken) return;

            const curr = String(this.router?.url || this.currentUrl || '');
            // If we've already landed on the dashboard route, no rescue is needed.
            if (this.isDashboardRoute(curr)) {
                return;
            }

            // Track consecutive blank-outlet frames.
            if (this.isMainOutletBlank()) {
                blankStreak++;
            } else {
                blankStreak = 0;
            }

            // If the outlet is blank for a few consecutive checks, treat it as the stuck state and rescue.
            if (blankStreak >= 3) {
                // If there are unsaved changes, prioritize restoring the current page rather than forcing dashboard.
                let isDirty = false;
                try { isDirty = this.cardState?.isAnyDirty?.() === true; } catch { isDirty = false; }
                if (isDirty) {
                    this.forceCurrentRouteRescueNavigation();
                    return;
                }
                this.forceDashboardRescueNavigation();
                return;
            }

            ticks++;
            if (ticks < MAX_TICKS) {
                setTimeout(tick, DELAY_MS);
            }
        };

        setTimeout(tick, DELAY_MS);
    }

    private isDashboardRendered(): boolean {
        try {
            // Dashboard host element selector
            return !!document.querySelector('app-care-manager-dashboard');
        } catch {
            return true; // be conservative; don't trigger rescue if DOM isn't accessible
        }
    }

    private isPatientEditRendered(): boolean {
        try {
            // Patient Edit V2 host element selector (full-page route under AppLayout)
            return !!document.querySelector('app-patient-edit-2');
        } catch {
            return true;
        }
    }

    private isMainOutletBlank(): boolean {
        // The user-reported symptom is: topbar + footer remain, but the main outlet becomes empty.
        // Angular inserts the activated component host as the next *element* sibling of <router-outlet>.
        try {
            const outlet = document.querySelector('.layout-main router-outlet') as HTMLElement | null;
            if (!outlet) return false;
            return !outlet.nextElementSibling;
        } catch {
            return false;
        }
    }

    /**
     * After a canceled navigation toward the dashboard, confirm we still have a rendered page.
     * If the outlet is blank, perform the same internal "rescue" navigation users do manually
     * by clicking Home twice.
     */
    private scheduleDashboardCancelRescue(): void {
        const token = ++this.cancelRenderCheckToken;
        const MAX_CHECKS = 12;
        const DELAY_MS = 120;

        const check = (attempt: number) => {
            if (token !== this.cancelRenderCheckToken) return;

            // If the main outlet has any rendered component, we're not in the blank-outlet state.
            if (!this.isMainOutletBlank()) {
                this.stopDashboardNavLoader();
                return;
            }

            if (attempt < MAX_CHECKS) {
                setTimeout(() => check(attempt + 1), DELAY_MS);
                return;
            }

            // Still blank: if there are unsaved changes, prioritize restoring the current page.
            // Otherwise, proceed with dashboard rescue navigation (what the user intended).
            let isDirty = false;
            try { isDirty = this.cardState?.isAnyDirty?.() === true; } catch { isDirty = false; }
            if (isDirty) {
                this.forceCurrentRouteRescueNavigation();
                return;
            }
            this.forceDashboardRescueNavigation();
        };

        setTimeout(() => check(0), DELAY_MS);
    }

    private forceCurrentRouteRescueNavigation(): void {
        // Restore the current route view without changing the address bar.
        // This is a safety net for the rare blank-outlet state after a canceled navigation.
        try { this.appLoader.start('nav-dashboard', 'Restoring page…'); } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }

        const curr = String(this.router?.url || this.currentUrl || '').trim();
        if (!curr) {
            this.stopDashboardNavLoader();
            return;
        }

        // Force a distinct internal navigation (dummy query param) while keeping URL stable.
        const base = curr.split('#')[0];
        const sep = base.includes('?') ? '&' : '?';
        const target = `${base}${sep}__r=${Date.now()}`;

        this.zone.run(() => {
            this.router
                .navigateByUrl(target, { skipLocationChange: true })
                .finally(() => this.stopDashboardNavLoader());
        });
    }

    private forceDashboardRescueNavigation(): void {
        // Avoid infinite loops if something deeper is wrong; user can still click Home manually.
        const now = Date.now();
        if (this.dashboardRescueInFlight) return;
        if (this.dashboardRescueCooldownUntilTs && now < this.dashboardRescueCooldownUntilTs) return;

        this.dashboardRescueInFlight = true;
        this.isNavigatingToDashboard = true;
        this.dashboardRescueCooldownUntilTs = now + 10_000;

        try {
            this.appLoader.start('nav-dashboard', 'Restoring dashboard…');
        } catch (error) {
      console.warn('[App] Non-fatal error', error);
    }

        const curr = String(this.router?.url || this.currentUrl || '');
        const alreadyOnDashboard = this.isDashboardRoute(curr);

        // If we're already on the dashboard URL but it didn't render, force a distinct internal navigation
        // (query param) while keeping the URL stable (skipLocationChange).
        // If we're still on Patient Edit (URL didn't change), navigate normally to the dashboard so the
        // address bar updates on the first click.
        const rescueTree = alreadyOnDashboard
            ? this.router.createUrlTree(['/genus-connect', 'care-manager-dashboard'], { queryParams: { refresh: Date.now() } })
            : this.router.createUrlTree(['/genus-connect', 'care-manager-dashboard']);

        this.zone.run(() => {
            const navPromise = alreadyOnDashboard
                ? this.router.navigateByUrl(rescueTree, { skipLocationChange: true })
                : this.router.navigateByUrl(rescueTree);
            navPromise
                .finally(() => {
                    this.dashboardRescueInFlight = false;
                    this.stopDashboardNavLoader();
                });
        });
    }

    /**
     * UX hardening: occasionally the router outlet can end up empty after navigating to Patient Edit V2.
     * When that happens (topbar still visible, content blank), re-navigate to the same route internally.
     */
    private schedulePatientEditRenderRescueIfNeeded(): void {
        const token = ++this.patientEditRenderCheckToken;
        const MAX_CHECKS = 12;
        const DELAY_MS = 120;

        const check = (attempt: number) => {
            if (token !== this.patientEditRenderCheckToken) return;

            const curr = String(this.router?.url || this.currentUrl || '');
            if (!this.isPatientEditV2Url(curr)) return;

            const hasOutlet = !this.isMainOutletBlank();
            const hasPatientEdit = this.isPatientEditRendered();
            if (hasOutlet && hasPatientEdit) {
                return;
            }

            if (attempt < MAX_CHECKS) {
                setTimeout(() => check(attempt + 1), DELAY_MS);
                return;
            }

            this.forcePatientEditRescueNavigation(curr);
        };

        setTimeout(() => check(0), DELAY_MS);
    }

    private forcePatientEditRescueNavigation(currentUrl: string): void {
        const now = Date.now();
        if (this.patientEditRescueInFlight) return;
        if (this.patientEditRescueCooldownUntilTs && now < this.patientEditRescueCooldownUntilTs) return;

        this.patientEditRescueInFlight = true;
        this.patientEditRescueCooldownUntilTs = now + 10_000;

        try {
            this.appLoader.start('patient-edit', 'Restoring patient edit…');
        } catch {}

        const base = String(currentUrl || '').split('#')[0];
        if (!base) {
            this.patientEditRescueInFlight = false;
            try { this.appLoader.stop('patient-edit'); } catch {}
            return;
        }

        const sep = base.includes('?') ? '&' : '?';
        const target = `${base}${sep}__r=${Date.now()}`;

        this.zone.run(() => {
            this.router
                .navigateByUrl(target, { skipLocationChange: true })
                .finally(() => {
                    this.patientEditRescueInFlight = false;
                    try { this.appLoader.stop('patient-edit'); } catch {}
                });
        });
    }

    private schedulePatientEditCancelRescue(cancelledTarget: string): void {
        const token = ++this.patientEditRenderCheckToken;
        const MAX_CHECKS = 12;
        const DELAY_MS = 120;

        const check = (attempt: number) => {
            if (token !== this.patientEditRenderCheckToken) return;

            const target = String(cancelledTarget || '');
            if (!this.isPatientEditV2Url(target)) return;

            const hasOutlet = !this.isMainOutletBlank();
            const hasPatientEdit = this.isPatientEditRendered();
            if (hasOutlet && hasPatientEdit) {
                try { this.appLoader.stop('patient-edit'); } catch {}
                return;
            }

            if (attempt < MAX_CHECKS) {
                setTimeout(() => check(attempt + 1), DELAY_MS);
                return;
            }

            this.forcePatientEditRescueNavigation(target);
        };

        setTimeout(() => check(0), DELAY_MS);
    }

    /**
     * Continuous health check while on Patient Edit V2.
     * If the outlet goes blank after initial render, re-navigate to restore.
     */
    private startPatientEditHealthCheck(): void {
        const token = ++this.patientEditHealthCheckToken;
        const DELAY_MS = 250;
        let blankStreak = 0;

        const tick = () => {
            if (token !== this.patientEditHealthCheckToken) return;

            const curr = String(this.router?.url || this.currentUrl || '');
            if (!this.isPatientEditV2Url(curr)) return;

            const hasOutlet = !this.isMainOutletBlank();
            const hasPatientEdit = this.isPatientEditRendered();
            if (hasOutlet && hasPatientEdit) {
                blankStreak = 0;
            } else {
                blankStreak++;
            }

            if (blankStreak >= 3) {
                this.forcePatientEditRescueNavigation(curr);
                blankStreak = 0;
            }

            setTimeout(tick, DELAY_MS);
        };

        setTimeout(tick, DELAY_MS);
    }

    private stopPatientEditHealthCheck(): void {
        this.patientEditHealthCheckToken++;
    }

    /**
     * UX hardening: occasionally the router outlet can end up empty after returning to the dashboard.
     * When that happens users report needing to click Home/Back twice. We detect the "blank outlet"
     * (dashboard route active but dashboard host not rendered) and force a single re-navigation.
     */
    private scheduleDashboardRenderRescueIfNeeded(): void {
        const url = String(this.currentUrl || this.router?.url || '');
        if (!this.isDashboardRoute(url)) {
            this.dashboardRenderCheckToken++;
            return;
        }

        const token = ++this.dashboardRenderCheckToken;
        const MAX_CHECKS = 10;
        const DELAY_MS = 120;

        const check = (attempt: number) => {
            if (token !== this.dashboardRenderCheckToken) return;
            const curr = String(this.currentUrl || this.router?.url || '');
            if (!this.isDashboardRoute(curr)) return;

            if (this.isDashboardRendered()) {
                return;
            }

            if (attempt < MAX_CHECKS) {
                setTimeout(() => check(attempt + 1), DELAY_MS);
                return;
            }

            // Still not rendered — run a single SPA "rescue" navigation.
            this.forceDashboardRescueNavigation();
        };

        setTimeout(() => check(0), DELAY_MS);
    }

    private isPatientEditV2Route(): boolean {
        try {
            const url = String(this.router?.url || this.currentUrl || '');
            return this.isPatientEditV2Url(url);
        } catch {
            return false;
        }
    }

    private isPatientEditV2Url(url: string): boolean {
        const u = String(url || '');
        return u.includes('genus-connect/patient-edit-2') || u.includes('patient-edit-2');
    }

    private prefetchDashboardChunk(): void {
        if (this.dashboardPrefetchPromise) return;
        // Prefetch the dashboard standalone component chunk so back/home feels instant.
        // This avoids the “blank outlet while chunk loads” effect that caused “click twice”.
        this.dashboardPrefetchPromise = import('../../views/genus-connect/care-manager-dashboard/care-manager-dashboard.component').catch(
            () => null
        );
    }

    private markDashboardReturnRefreshOnce(): void {
        try {
            sessionStorage.setItem('care-manager-dashboard:return-refresh-once', '1');
        } catch {
            // no-op
        }
    }

    toggleDarkMode() {
        this.layoutService.layoutConfig.update((state) => ({ ...state, darkTheme: !state.darkTheme }));
    }

    toggle(event: Event) {
        this.op.toggle(event);
    }

    redirectToPage(page:string){
        console.log(page)
        if(page == 'profile'){
          this.router.navigate(['account/edit-profile'],{});
        }else if(page == 'change-password'){
          this.router.navigate(['account/change-password']);
        }
    }
    
    /**
     * Initialize tour menu items
     */
    private initTourItems() {
        this.tourItems = [
            {
                label: 'Intro',
                icon: 'pi pi-info-circle',
                command: () => this.startIntroTour()
            },
            {
                label: 'Advanced',
                icon: 'pi pi-cog',
                command: () => this.startAdvancedTour()
            }
        ];
    }
    
    /**
     * Start the intro tour for the current component
     */
    startIntroTour() {
        this.tourService.startIntroTour();
    }
    
    /**
     * Start the advanced tour for the current component
     */
    startAdvancedTour() {
        this.tourService.startAdvancedTour();
    }
    
    /**
     * End the current tour
     */
    endTour() {
        this.tourService.endTour();
    }
    
    /**
     * Check if tours have been loaded and if a tour is available for the current route
     */
    private checkToursLoaded() {
        // First check if tours are loaded
        const checkLoading = setInterval(() => {
            if (this.tourService.areToursLoaded()) {
                this.toursLoaded = true;
                this.checkTourAvailability();
                clearInterval(checkLoading);
                
                // Set up route change listener after tours are loaded
                this.router.events.subscribe(() => {
                    this.checkTourAvailability();
                });
            }
        }, 500); // Check every 500ms
    }
    
    /**
     * Check if a tour is available for the current route
     */
    private checkTourAvailability() {
        this.hasTourAvailable = this.tourService.hasTourForCurrentRoute();
    }

    /**
     * Show the help modal for the current page
     */
    showHelp() {
        if (this.helpModal) {
            this.helpModal.openHelpModal();
        }
    }

    openAIAssistant() {
        if (this.aiAssistantChatbot) {
            this.aiAssistantChatbot.openChatbot();
        }
    }
} 